import type { ModuleKey } from "../modules/registry";
import type { Permission, Role } from "./types";

/**
 * PropertyId is a string so that IDs sourced from the OPS DB at runtime
 * are accepted without needing a compile-time enumeration.
 */
export type PropertyId = string;

export type PropertyOption = {
  id: PropertyId;
  label: string;
  isDefault?: boolean;
};

export type AccessSubcategory = {
  key: string;
  label: string;
  permissions: Permission[];
};

export type AccessModuleDefinition = {
  key: ModuleKey;
  label: string;
  description: string;
  permissions: Permission[];
  subcategories: AccessSubcategory[];
};

export type PropertyAccessAssignment = {
  propertyId: string;
  modules: Partial<Record<ModuleKey, string[]>>;
};

/**
 * HARDENED: Property options are now dynamic and loaded from server.
 * This static array should be empty in production. Properties must be loaded
 * from the OPS database at runtime, not hardcoded in the frontend.
 * 
 * Fallback behavior: UI components have emergency fallbacks to prevent crashes,
 * but production deployments must configure proper backend property provisioning.
 */
export const PROPERTY_OPTIONS: PropertyOption[] = [];

export const ACCESS_MODULES: AccessModuleDefinition[] = [
  {
    key: "pms",
    label: "PMS",
    description: "Reservations, front office, guest profiles and folio workflows.",
    permissions: ["pms.access"],
    subcategories: [
      {
        key: "frontdesk",
        label: "Front Desk",
        permissions: [
          "pms.incidents.update",
          "pms.rooms.view", "pms.rooms.assign",
          "pms.housekeeping.assign",
          "pms.reservations.view", "pms.reservations.checkin", "pms.reservations.checkout",
          "pms.guests.view",
          "pms.billing.view", "pms.billing.post", "pms.billing.payment",
        ],
      },
      {
        key: "reservations",
        label: "Reservations",
        permissions: [
          "pms.reservations.view", "pms.reservations.create", "pms.reservations.edit",
          "pms.reservations.cancel", "pms.reservations.noshow",
          "pms.groups.view",
          "pms.guests.view", "pms.guests.create", "pms.guests.edit",
          "pms.rooms.view",
          "pms.rates.view",
        ],
      },
      {
        key: "groups_blocks",
        label: "Groups & Blocks",
        permissions: [
          "pms.groups.view", "pms.groups.create", "pms.groups.edit", "pms.groups.cancel",
          "pms.groups.inventory.manage", "pms.groups.inventory.override",
          "pms.groups.rates.manage", "pms.groups.rates.override",
          "pms.groups.release", "pms.groups.cutoff.override",
          "pms.groups.rooming_list.manage", "pms.groups.rooming_list.import",
          "pms.groups.billing.manage", "pms.groups.payments.manage",
          "pms.groups.documents.manage", "pms.groups.audit.view",
          "pms.groups.sales.view", "pms.groups.sales.manage",
        ],
      },
      {
        key: "housekeeping",
        label: "Housekeeping",
        permissions: ["pms.housekeeping.view", "pms.housekeeping.update", "pms.housekeeping.assign", "pms.rooms.view"],
      },
      {
        key: "billing",
        label: "Billing",
        permissions: ["pms.billing.view", "pms.billing.post", "pms.billing.payment", "pms.reservations.view"],
      },
      {
        key: "billing_advanced",
        label: "Advanced Billing",
        permissions: [
          "pms.billing.discount", "pms.billing.void", "pms.billing.comp",
          "pms.billing.transfer", "pms.billing.close",
        ],
      },
      {
        key: "rates",
        label: "Rate Management",
        permissions: ["pms.rates.view", "pms.rates.override"],
      },
      {
        key: "nightaudit",
        label: "Night Audit",
        permissions: ["pms.nightaudit.run", "pms.nightaudit.view"],
      },
      {
        key: "reports",
        label: "Reports",
        permissions: ["pms.reports.view", "pms.reports.financial", "reports.view"],
      },
    ],
  },
  {
    key: "pos",
    label: "POS",
    description: "Outlet sales, cashier, till management and POS reporting.",
    permissions: ["pos.access"],
    subcategories: [
      {
        key: "sales",
        label: "Sales",
        permissions: ["pos.sales.view", "pos.sales.create", "pos.sales.edit", "pos.tables.view"],
      },
      {
        key: "cashier",
        label: "Cashier & Till",
        permissions: ["pos.cashier.open", "pos.cashier.payment", "pos.cashier.refund", "pos.sales.view"],
      },
      {
        key: "management",
        label: "POS Management",
        permissions: [
          "pos.sales.void", "pos.sales.discount", "pos.sales.comp",
          "pos.menu.view", "pos.menu.edit", "pos.tables.assign",
        ],
      },
      {
        key: "reports",
        label: "POS Reports",
        permissions: ["pos.reports.view", "pos.reports.financial"],
      },
    ],
  },
  {
    key: "ems",
    label: "EMS",
    description: "Engineering, technical systems and asset management.",
    permissions: ["ems.access"],
    subcategories: [
      {
        key: "operations",
        label: "Operations",
        permissions: ["ems.assets.view", "ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit"],
      },
      {
        key: "management",
        label: "Management",
        permissions: ["ems.assets.edit", "ems.reports.view"],
      },
    ],
  },
  {
    key: "voip",
    label: "VoIP",
    description: "Telephony operations, extensions and queue configuration.",
    permissions: ["voip.access"],
    subcategories: [
      {
        key: "operations",
        label: "Operations",
        permissions: ["voip.calls.view", "voip.calls.manage"],
      },
      {
        key: "configuration",
        label: "Configuration",
        permissions: ["voip.config.view", "voip.config.edit"],
      },
    ],
  },
  {
    key: "wms",
    label: "WMS",
    description: "Warehouse, purchase orders and stock management.",
    permissions: ["wms.access"],
    subcategories: [
      {
        key: "inventory",
        label: "Inventory",
        permissions: ["wms.inventory.view", "wms.inventory.adjust", "wms.orders.view", "wms.orders.receive", "wms.suppliers.view"],
      },
      {
        key: "procurement",
        label: "Procurement",
        permissions: ["wms.orders.create", "wms.orders.approve", "wms.suppliers.edit"],
      },
    ],
  },
  {
    key: "revenue",
    label: "Revenue",
    description: "Revenue insights, pricing controls and forecasting.",
    permissions: ["revenue.access"],
    subcategories: [
      {
        key: "analytics",
        label: "Analytics",
        permissions: ["revenue.analytics.view", "revenue.analytics.export"],
      },
      {
        key: "pricing",
        label: "Pricing",
        permissions: ["revenue.pricing.view", "revenue.pricing.edit"],
      },
    ],
  },
  {
    key: "guests",
    label: "Guests",
    description: "Guest profiles, communication and loyalty management.",
    permissions: ["guests.access"],
    subcategories: [
      {
        key: "profiles",
        label: "Profiles",
        permissions: ["guests.profiles.view", "guests.profiles.edit"],
      },
      {
        key: "communication",
        label: "Communication",
        permissions: ["guests.communication.view", "guests.communication.send"],
      },
      {
        key: "loyalty",
        label: "Loyalty",
        permissions: ["guests.loyalty.view", "guests.loyalty.edit"],
      },
    ],
  },
  {
    key: "cctv",
    label: "CCTV",
    description: "Property video surveillance and incident management.",
    permissions: ["cctv.access"],
    subcategories: [
      {
        key: "monitoring",
        label: "Monitoring",
        permissions: ["cctv.live.view", "cctv.recordings.view"],
      },
      {
        key: "incidents",
        label: "Incidents",
        permissions: ["cctv.incidents.view", "cctv.incidents.manage"],
      },
    ],
  },
  {
    key: "keycoder",
    label: "Keycoder",
    description: "Access control devices, card issuance and device administration.",
    permissions: ["keycoder.access"],
    subcategories: [
      {
        key: "issue_cards",
        label: "Issue Cards",
        permissions: ["keycoder.cards.issue", "keycoder.cards.cancel"],
      },
      {
        key: "device_admin",
        label: "Device Admin",
        permissions: ["keycoder.devices.view", "keycoder.devices.manage"],
      },
    ],
  },
  {
    key: "garage",
    label: "Garage",
    description: "Parking inventory and property parking operations.",
    permissions: ["garage.access"],
    subcategories: [
      {
        key: "operations",
        label: "Operations",
        permissions: ["garage.parking.view", "garage.parking.manage"],
      },
      {
        key: "settings",
        label: "Garage Settings",
        permissions: ["garage.settings.view", "garage.settings.edit"],
      },
    ],
  },
];

export const ACCESS_MODULES_BY_KEY = Object.fromEntries(
  ACCESS_MODULES.map((definition) => [definition.key, definition]),
) as Record<ModuleKey, AccessModuleDefinition>;

/**
 * Filters `allOptions` to the subset identified by `propertyIds`.
 * Pass `undefined` or empty to return the full list.
 */
export function getPropertyOptions(
  propertyIds?: string[],
  allOptions: PropertyOption[] = PROPERTY_OPTIONS,
): PropertyOption[] {
  if (!propertyIds?.length) return allOptions;
  const allowed = new Set(propertyIds);
  const filtered = allOptions.filter((property) => allowed.has(property.id));
  return filtered.length ? filtered : allOptions;
}

function collectModulePermissions(moduleKey: ModuleKey, subcategoryKeys?: string[]) {
  const definition = ACCESS_MODULES_BY_KEY[moduleKey];
  if (!definition) return [];
  if (!subcategoryKeys?.length) return [...definition.permissions];

  const permissionSet = new Set(definition.permissions);
  for (const subcategory of definition.subcategories) {
    if (subcategoryKeys.includes(subcategory.key)) {
      for (const permission of subcategory.permissions) {
        permissionSet.add(permission);
      }
    }
  }
  return [...permissionSet];
}

export function resolveAssignmentPermissions(assignments: PropertyAccessAssignment[] = []) {
  const permissionSet = new Set<Permission>();
  for (const assignment of assignments) {
    for (const [moduleKey, subcategories] of Object.entries(assignment.modules) as Array<[ModuleKey, string[] | undefined]>) {
      for (const permission of collectModulePermissions(moduleKey, subcategories)) {
        permissionSet.add(permission);
      }
    }
  }
  return [...permissionSet];
}

/**
 * Builds default PropertyAccessAssignment[] for the given role scoped to propertyIds.
 * HARDENED: When properties are dynamic (loaded from server), static fallbacks are avoided.
 * Users with no propertyIds/propertyAccess will have restricted access until dynamically provisioned.
 */
export function buildDefaultAssignments(
  role?: Role,
  propertyIds: string[] = [],
  allOptions: PropertyOption[] = PROPERTY_OPTIONS,
): PropertyAccessAssignment[] {
  const scopedProperties = getPropertyOptions(propertyIds, allOptions).map((p) => p.id);
  
  // IMPORTANT: Do not fallback to hardcoded properties when scopedProperties is empty.
  // If allOptions is empty (production case where properties are server-sourced),
  // users with no assigned properties get no access. This is the correct secure behavior.
  const targets = scopedProperties.length 
    ? scopedProperties 
    : allOptions.length > 0 
      ? [allOptions[0].id]  // Only fallback if allOptions is populated (dev/test)
      : [];  // Production case: empty list means no property access until server provisions it

  // If no target properties, return empty assignments (no property access)
  if (!targets.length) {
    return [];
  }

  const byRole: Partial<Record<Role, Partial<Record<ModuleKey, string[]>>>> = {
    admin: Object.fromEntries(
      ACCESS_MODULES.map((m) => [m.key, m.subcategories.map((s) => s.key)]),
    ) as Partial<Record<ModuleKey, string[]>>,

    general_manager: {
      pms: ["frontdesk", "reservations", "housekeeping", "billing", "billing_advanced", "rates", "nightaudit", "reports"],
      pos: ["sales", "cashier", "management", "reports"],
      revenue: ["analytics", "pricing"],
      guests: ["profiles", "communication", "loyalty"],
    },

    manager: {
      pms: ["frontdesk", "reservations", "housekeeping", "billing", "rates", "reports"],
      pos: ["sales", "cashier", "reports"],
      revenue: ["analytics"],
      guests: ["profiles"],
    },

    supervisor: {
      pms: ["frontdesk", "reservations", "housekeeping", "billing", "reports"],
      pos: ["sales", "cashier"],
    },

    front_office: {
      pms: ["frontdesk", "reservations", "billing"],
    },

    housekeeping: {
      pms: ["housekeeping"],
    },

    finance: {
      pms: ["billing", "billing_advanced", "nightaudit", "reports"],
      revenue: ["analytics"],
    },

    engineering: {
      ems: ["operations", "management"],
    },

    staff: {
      pms: ["frontdesk"],
      pos: ["sales", "cashier"],
    },
  };

  const template = byRole[role ?? "user"] ?? {};
  return targets.map((propertyId) => ({ propertyId, modules: template }));
}
